
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {TickManagerUpdate} from '../core/TickManagerUpdate';
import {FieldModelTickManagerUpdate} from '../core/TickManagerUpdate';

/**
 * FeedUpdateTickResponse struct
 */
class FeedUpdateTickResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!Date=} time
   * @param {!Array=} ticks
   * @constructor
   */
  constructor (parent = new Response(), time = new Date(0), ticks = []) {
    super()
    super.copy(parent)
    this.Time = time
    this.Ticks = ticks
  }

  /**
   * Copy struct (shallow copy)
   * @this {!FeedUpdateTickResponse}
   * @param {!FeedUpdateTickResponse} other Other struct
   * @returns {!FeedUpdateTickResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Time != null) {
      if (other.Time instanceof Date) {
        this.Time = new Date(other.Time.getTime())
      } else {
        this.Time = new Date(Math.round(other.Time / 1000000))
      }
    } else {
      this.Time = null
    }
    if (other.Ticks != null) {
      this.Ticks = []
      for (let item of other.Ticks) {
        if (item != null) {
          let tempItem
          tempItem = TickManagerUpdate.fromObject(item)
          this.Ticks.push(tempItem)
        } else {
          this.Ticks.push(null)
        }
      }
    } else {
      this.Ticks = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!FeedUpdateTickResponse}
   * @returns {!FeedUpdateTickResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new FeedUpdateTickResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new FeedUpdateTickResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!FeedUpdateTickResponse}
   * @param {!FeedUpdateTickResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof FeedUpdateTickResponse)) {
      throw new TypeError('Instance of FeedUpdateTickResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!FeedUpdateTickResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Time: ((this.Time != null) ? (this.Time.getTime() * 1000000) : null),
      Ticks: ((this.Ticks != null) ? Array.from(this.Ticks, item => ((item != null) ? item : null)) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return FeedUpdateTickResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!FeedUpdateTickResponse} other Object value
   * @returns {!FeedUpdateTickResponse} Created struct
   */
  static fromObject (other) {
    return new FeedUpdateTickResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!FeedUpdateTickResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return FeedUpdateTickResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!FeedUpdateTickResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6351
  }
}

export { FeedUpdateTickResponse };

/**
 * Fast Binary Encoding FeedUpdateTickResponse field model
 */
class FieldModelFeedUpdateTickResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.Time = new fbe.FieldModelTimestamp(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Ticks = new fbe.FieldModelVector(new FieldModelTickManagerUpdate(buffer, this.Time.fbeOffset + this.Time.fbeSize), buffer, this.Time.fbeOffset + this.Time.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelFeedUpdateTickResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelFeedUpdateTickResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelFeedUpdateTickResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Time.fbeSize + this.Ticks.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelFeedUpdateTickResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Time.fbeExtra + this.Ticks.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelFeedUpdateTickResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelFeedUpdateTickResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelFeedUpdateTickResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6351
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelFeedUpdateTickResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelFeedUpdateTickResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Time.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Time.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.Ticks.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Ticks.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ticks.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelFeedUpdateTickResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelFeedUpdateTickResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelFeedUpdateTickResponse}
   * @param {!FeedUpdateTickResponse} fbeValue Default value, defaults is new FeedUpdateTickResponse()
   * @returns {!FeedUpdateTickResponse} FeedUpdateTickResponse value
   */
  get (fbeValue = new FeedUpdateTickResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelFeedUpdateTickResponse}
   * @param {!FeedUpdateTickResponse} fbeValue FeedUpdateTickResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Time.fbeSize) <= fbeStructSize) {
      fbeValue.Time = this.Time.get()
    } else {
      fbeValue.Time = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.Ticks.fbeSize) <= fbeStructSize) {
      this.Ticks.get(fbeValue.Ticks)
    } else {
      fbeValue.Ticks.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ticks.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelFeedUpdateTickResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelFeedUpdateTickResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelFeedUpdateTickResponse}
   * @param {!FeedUpdateTickResponse} fbeValue FeedUpdateTickResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelFeedUpdateTickResponse}
   * @param {FeedUpdateTickResponse} fbeValue FeedUpdateTickResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Time.set(fbeValue.Time)
    this.Ticks.set(fbeValue.Ticks)
  }
}

export { FieldModelFeedUpdateTickResponse };

/**
 * Fast Binary Encoding FeedUpdateTickResponse model
 */
class FeedUpdateTickResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelFeedUpdateTickResponse(this.buffer, 4)
  }

  /**
   * Get the FeedUpdateTickResponse model
   * @this {!FeedUpdateTickResponseModel}
   * @returns {!FieldModelFeedUpdateTickResponse} model FeedUpdateTickResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!FeedUpdateTickResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!FeedUpdateTickResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return FeedUpdateTickResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!FeedUpdateTickResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelFeedUpdateTickResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!FeedUpdateTickResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!FeedUpdateTickResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!FeedUpdateTickResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!FeedUpdateTickResponseModel}
   * @param {!FeedUpdateTickResponse} value FeedUpdateTickResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!FeedUpdateTickResponseModel}
   * @param {!FeedUpdateTickResponse} value FeedUpdateTickResponse value, defaults is new FeedUpdateTickResponse()
   * @return {!object} Deserialized FeedUpdateTickResponse value and its size
   */
  deserialize (value = new FeedUpdateTickResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new FeedUpdateTickResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new FeedUpdateTickResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!FeedUpdateTickResponseModel}
   * @param {!number} prev Previous FeedUpdateTickResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { FeedUpdateTickResponseModel };
